/* eslint-disable */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import DashIcon from "components/icons/DashIcon";
import { useAppLanguage } from "hooks/useAppLanguage";

function fullRoutePath(route: RoutesType): string {
  return `${route.layout}/${route.path}`.replace(/\/+/g, "/");
}

export const SidebarLinks = (props: {
  routes: RoutesType[];
  sidebarLayout: "/admin" | "/instructor";
  portal?: "admin" | "instructor";
}): JSX.Element => {
  const location = useLocation();
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { routes, sidebarLayout, portal = "admin" } = props;
  const portalActiveClass = portal === "instructor" ? "bg-orange-500/10 dark:bg-orange-400/10" : "bg-emerald-500/10 dark:bg-emerald-400/10";
  const portalAccentClass = portal === "instructor" ? "bg-orange-500 dark:bg-orange-400" : "bg-emerald-500 dark:bg-emerald-400";
  const portalTextClass = portal === "instructor" ? "text-orange-600 dark:text-orange-300" : "text-emerald-600 dark:text-emerald-300";
  const activeRef = React.useRef<HTMLAnchorElement | null>(null);

  const isActive = (fullPath: string) =>
    location.pathname === fullPath || location.pathname.startsWith(`${fullPath}/`);

  // Bring the active item into view once after mount (e.g. deep links low in the list)
  React.useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createLinks = (routes: RoutesType[]) => {
    return routes
      .filter((route) => route.layout === sidebarLayout && route.hideInSidebar !== true)
      .map((route, index) => {
        const to = fullRoutePath(route);
        const active = isActive(to);
        const label = language === "ar" ? route.nameAr || route.name : route.nameEn || route.name;

        return (
          <Link key={index} to={to} ref={active ? activeRef : undefined}>
            <div
              className={`apple-nav-item relative mb-1 cursor-pointer rounded-xl px-2 py-1 transition-colors ${
                active
                  ? `apple-nav-item-active ${portalActiveClass}`
                  : "hover:bg-gray-100 dark:hover:bg-white/[0.04]"
              }`}
            >
              <li
                dir={isArabic ? "rtl" : "ltr"}
                className="my-[2px] flex w-full cursor-pointer items-center gap-3 px-3 py-2"
              >
                <span
                  className={`shrink-0 ${
                    active
                      ? `font-bold ${portalTextClass}`
                      : "font-medium text-gray-500 dark:text-slate-400"
                  }`}
                >
                  {route.icon ? route.icon : <DashIcon />}
                </span>
                <p
                  className={`min-w-0 flex-1 truncate leading-none ${
                    isArabic ? "text-right" : "text-left"
                  } ${
                    active
                      ? `font-bold text-navy-700 dark:text-slate-50 ${portalTextClass}`
                      : "font-medium text-gray-600 dark:text-slate-400"
                  }`}
                >
                  {label}
                </p>
              </li>
              {active ? (
                <div
                  aria-hidden
                    className={`absolute top-2 bottom-2 w-1 rounded-full ${portalAccentClass} ${
                    isArabic ? "right-1" : "left-1"
                  }`}
                />
              ) : null}
            </div>
          </Link>
        );
      });
  };

  return <>{createLinks(routes)}</>;
};

export default SidebarLinks;
